import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";

import JsonTreeView from "./JsonTreeView";
import "./ConfigViewerModal.scss";

function stringifyError(err) {
  if (!err) return "Unknown error";

  // Axios errors
  const status = err.response?.status;
  const statusText = err.response?.statusText;
  const responseData = err.response?.data;

  if (status) {
    let extra = "";
    if (typeof responseData === "string") {
      extra = responseData;
    } else if (responseData) {
      extra = JSON.stringify(responseData, null, 2);
    }
    return `HTTP ${status}${statusText ? ` (${statusText})` : ""}${extra ? `\n\n${extra}` : ""}`;
  }

  if (err.message) return err.message;
  return String(err);
}

function safePrettyJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

function ConfigViewerModal({ isOpen, toggle, title, resourceId, fetch, requestInfo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [treeExpandMode, setTreeExpandMode] = useState("default");
  const [treeExpandSignal, setTreeExpandSignal] = useState(0);

  const requestInfoText = useMemo(() => {
    if (!requestInfo) return null;
    return safePrettyJson(requestInfo);
  }, [requestInfo]);

  const load = useCallback(async () => {
    if (!fetch) return;

    setLoading(true);
    setError(null);
    try {
      const result = await fetch();
      setData(result);
    } catch (e) {
      setError(stringifyError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fetch]);

  useEffect(() => {
    if (!isOpen) return;
    load();
  }, [isOpen, resourceId, load]);

  useEffect(() => {
    if (!isOpen) return;
    setTreeExpandMode("default");
    setTreeExpandSignal((x) => x + 1);
  }, [isOpen, resourceId]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(safePrettyJson(data));
      toast.success("Copied to clipboard!");
    } catch (e) {
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" contentClassName="config-viewer-modal">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        {requestInfoText && (
          <div className="mb-3">
            <div className="text-muted mb-1">Request</div>
            <pre className="mb-0">{requestInfoText}</pre>
          </div>
        )}

        {loading && (
          <div className="d-flex align-items-center gap-2">
            <Spinner size="sm" color="primary" /> <span>Loading…</span>
          </div>
        )}

        {!loading && error && (
          <Alert color="danger">
            <pre className="mb-0">{error}</pre>
          </Alert>
        )}

        {!loading && !error && data == null && <div className="text-muted">No data</div>}

        {!loading && !error && data != null && (
          <JsonTreeView data={data} expandMode={treeExpandMode} expandSignal={treeExpandSignal} />
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          <Button color="warning" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setTreeExpandMode("all");
              setTreeExpandSignal((x) => x + 1);
            }}
            disabled={loading || data == null}
          >
            Expand all
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setTreeExpandMode("none");
              setTreeExpandSignal((x) => x + 1);
            }}
            disabled={loading || data == null}
          >
            Collapse all
          </Button>
        </div>
        <div className="d-flex gap-2">
          <Button color="primary" onClick={copyToClipboard} disabled={loading || data == null}>
            Copy JSON
          </Button>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

ConfigViewerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetch: PropTypes.func.isRequired,
  requestInfo: PropTypes.shape({
    url: PropTypes.string,
    headers: PropTypes.objectOf(PropTypes.string),
  }),
};

ConfigViewerModal.defaultProps = {
  resourceId: null,
  requestInfo: null,
};

export default ConfigViewerModal;
